var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useCallback, useMemo, useReducer, useState } from 'react';
import { Grid, GridColumn as Column, GridToolbar } from '@progress/kendo-react-grid';
import FilterPanel from '@components/FilterPanel';
import EnterKeyPager from '@components/Kendo/EnterKeyPager';
import { translate } from '@utils/i18n';
import { ModeIA } from '@features/IIMReview/types';
import { saveCacheToLocalStorage } from '../../helpers/localStorage';
import kendoDatatableParams from '../../helpers/kendoDatatableParams';
import ColumnMenu from './nested/ColumnMenu';
import ColumnMenuContext from './nested/ColumnMenuContext';
import StatusCards from './components/StatusCards';
import BulkActions from './components/BulkActions';
import BulkCompleteReviewModal from './components/BulkCompleteReviewModal';
import { InspectionIdCell, BuildingCell, ActionsCell, InspectionStatusCell, PriorityCell } from './cells';
import { calculateColumnWidths } from '../InspectionsIndex/helpers/columnWidth';
import useInspectionsReview from './hooks/useInspectionsReview';
import { usePublishListContext } from '@features/AIChatWidget/hooks/usePublishListContext';
import './index.css';
import getFiltersList from './helpers/filtersList';
import { getInitialData, INITIAL_FILTERS } from './helpers/initialData';
import { getColumnsConfig, CACHE_KEY, ALL_COLUMNS } from './constants';
import { actionTypes, reducer } from './reducer';
import CustomScrollbar from '../../components/CustomScrollbar/CustomScrollbar';
import IaTeaserModeBanner from '../IaTeaserModeBanner';
function AiToReviewInspectionsIndex({ frequenciesForSelect, assetsForSelect, statusCounts, modeIa, currentUser, statusesForSelect }) {
    // Use the reducer with proper initialization
    const [state, dispatch] = useReducer(reducer, { cacheKey: CACHE_KEY }, getInitialData);
    // Destructure state with proper typing
    const { page, sort, filters, columns, data, totalCount, loading } = state;
    // Publish live grid state to the AI chat widget (page context overlay).
    usePublishListContext({ page, sort, filters, totalCount, data });
    // Selection state (local instead of reducer)
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [selectAll, setSelectAll] = useState(false);
    // Bulk complete modal state
    const [showBulkCompleteModal, setShowBulkCompleteModal] = useState(false);
    // Use React Query to fetch data
    const { data: queryData, error, isLoading, isFetching } = useInspectionsReview({
        page,
        sort,
        filters
    });
    useEffect(() => {
        saveCacheToLocalStorage(CACHE_KEY, { page, sort, filters, columns });
    }, [page, sort, filters, columns]);
    // Update local state when React Query data changes
    useEffect(() => {
        if (queryData) {
            dispatch({ type: actionTypes.DATA_LOADED, data: queryData });
            // Clear selection when data changes (page/filter/sort changes)
            setSelectedIds(new Set());
            setSelectAll(false);
        }
    }, [queryData]);
    // Update loading state based on React Query status
    useEffect(() => {
        dispatch({ type: actionTypes.LOADING_CHANGED, loading: isLoading || isFetching });
    }, [isLoading, isFetching]);
    // Handle errors from React Query
    useEffect(() => {
        if (error) {
            console.error('Error loading AI review inspections:', error);
        }
    }, [error]);
    const filtersList = useMemo(() => getFiltersList({
        filters,
        dispatch,
        frequenciesForSelect,
        assetsForSelect,
        statusesForSelect
    }), [filters, frequenciesForSelect, assetsForSelect, statusesForSelect]);
    const onColumnsChange = useCallback((cols) => dispatch({
        type: actionTypes.COLUMNS_CHANGED,
        columns: cols.map((col) => col.id).filter((id) => Boolean(id))
    }), []);
    const onResetFilters = useCallback(() => {
        dispatch({
            type: actionTypes.FILTER_CHANGED,
            filters: INITIAL_FILTERS
        });
        // Clear selected inspections when filters are reset
        setSelectedIds(new Set());
        setSelectAll(false);
    }, []);
    const onResetCache = () => {
        // eslint-disable-next-line no-alert
        if (!window.confirm(translate('are_you_sure', { namespace: 'generic' })))
            return;
        localStorage.removeItem(CACHE_KEY);
        window.location.reload();
    };
    const ALL_COLUMNS_DATA = useMemo(() => getColumnsConfig(InspectionIdCell, BuildingCell, ActionsCell, InspectionStatusCell, PriorityCell), []);
    const allMenuColumns = useMemo(() => ALL_COLUMNS.map((column) => ALL_COLUMNS_DATA[column]), []);
    const selectedMenuColumns = useMemo(() => columns.map((column) => ALL_COLUMNS_DATA[column]), [columns]);
    // Add 'selected' column as first column for checkbox selection (hidden in Teaser mode)
    // In Teaser mode, also remove --primary/--selector classes since they assume selector column exists
    const visibleColumns = useMemo(() => {
        const baseColumns = modeIa === ModeIA.Teaser ? columns : ['selected', ...columns];
        return baseColumns.map((column) => {
            var _a, _b;
            const columnConfig = ALL_COLUMNS_DATA[column];
            if (modeIa === ModeIA.Teaser) {
                // Strip sticky positioning classes that depend on selector column
                return Object.assign(Object.assign({}, columnConfig), { className: (_a = columnConfig.className) === null || _a === void 0 ? void 0 : _a.replace(/--primary|--selector/g, '').trim(), headerClassName: (_b = columnConfig.headerClassName) === null || _b === void 0 ? void 0 : _b.replace(/--primary|--selector/g, '').trim() });
            }
            return columnConfig;
        });
    }, [columns, ALL_COLUMNS_DATA, modeIa]);
    // Selection handlers using local state
    const onSelectionChange = useCallback((event) => {
        const { id } = event.dataItem;
        setSelectedIds((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            }
            else {
                newSet.add(id);
            }
            return newSet;
        });
        setSelectAll(false);
    }, []);
    const onHeaderSelectionChange = useCallback((event) => {
        const target = event.syntheticEvent.target;
        if (target.checked) {
            setSelectedIds(new Set(data.map((item) => item.id)));
        }
        else {
            setSelectedIds(new Set());
            setSelectAll(false);
        }
    }, [data]);
    const onSelectAll = useCallback(() => {
        setSelectAll(true);
        setSelectedIds(new Set(data.map((item) => item.id)));
    }, [data]);
    // Calculate header checkbox state
    const headerSelectionValue = selectAll || (totalCount > 0 && data.length > 0 && selectedIds.size === data.length);
    // Selected inspections for bulk actions
    const selectedInspections = useMemo(() => data.filter((inspection) => selectedIds.has(inspection.id)), [data, selectedIds]);
    // Data with selection state computed for the Grid
    const dataWithSelection = useMemo(() => data.map((item) => (Object.assign(Object.assign({}, item), { selected: selectedIds.has(item.id) }))), [data, selectedIds]);
    // Bulk complete handlers
    const handleBulkCompleteClick = useCallback(() => {
        setShowBulkCompleteModal(true);
    }, []);
    const handleBulkCompleteClose = useCallback(() => {
        setShowBulkCompleteModal(false);
    }, []);
    // Export success handler - clears selections after export
    const handleExportSuccess = useCallback(() => {
        setSelectedIds(new Set());
        setSelectAll(false);
    }, []);
    // Memoize calculated column widths - only recalculates when data changes
    const calculatedWidths = useMemo(() => {
        return calculateColumnWidths(data, visibleColumns);
    }, [data, visibleColumns]);
    const contextValue = useMemo(() => ({
        onColumnsChange,
        columnsState: selectedMenuColumns,
        columns: allMenuColumns,
        dispatch
    }), [onColumnsChange, selectedMenuColumns, allMenuColumns, dispatch]);
    const queryParams = useMemo(() => {
        const _a = filters.values, { asset } = _a, valuesFiltered = __rest(_a, ["asset"]);
        return kendoDatatableParams({
            page,
            sort,
            filters: Object.assign(Object.assign({}, filters), { values: valuesFiltered }),
            optionalParams: asset && Array.isArray(asset) && asset.length > 0 && { asset: asset.join(',') }
        }).toString();
    }, [page, sort, filters]);
    return (React.createElement(React.Fragment, null,
        React.createElement(StatusCards, { statusCounts: statusCounts }),
        React.createElement(ColumnMenuContext.Provider, { value: contextValue },
            React.createElement(FilterPanel, { onResetFilters: onResetFilters, onResetCache: onResetCache, filters: filtersList }),
            React.createElement("hr", { className: "workspace__split--sticky" }),
            loading && (React.createElement("div", { className: "workspace__section" },
                React.createElement("div", { className: "qmb-loading--96", "data-title": translate('loading', { namespace: 'generic' }) },
                    React.createElement("svg", { role: "img" },
                        React.createElement("use", { href: "/map.svg#load-spinner" }))))),
            !loading && (React.createElement(CustomScrollbar, null,
                React.createElement(Grid, { data: modeIa === ModeIA.Teaser ? data : dataWithSelection, total: totalCount, skip: page.skip, take: page.take, onPageChange: (event) => {
                        dispatch({ type: actionTypes.PAGE_CHANGED, page: event.page });
                    }, pageable: {
                        type: 'input',
                        pageSizes: [25, 50, 100],
                        pageSizeValue: page.take
                    }, pager: EnterKeyPager, sortable: true, sort: sort, onSortChange: (event) => {
                        dispatch({ type: actionTypes.SORT_CHANGED, sort: event.sort });
                    }, dataItemKey: "id", selectedField: modeIa !== ModeIA.Teaser ? 'selected' : undefined, onSelectionChange: modeIa !== ModeIA.Teaser ? onSelectionChange : undefined, onHeaderSelectionChange: modeIa !== ModeIA.Teaser ? onHeaderSelectionChange : undefined, className: "qmb-grid--01" },
                    React.createElement(GridToolbar, null,
                        React.createElement(BulkActions, { selectedInspections: selectedInspections, selectAll: selectAll, totalCount: totalCount, modeIa: modeIa, queryParams: queryParams, onSelectAll: onSelectAll, onBulkCompleteClick: handleBulkCompleteClick, onExportSuccess: handleExportSuccess })),
                    visibleColumns.map((column) => (React.createElement(Column, { key: column.id, field: column.field, title: column.title, columnMenu: ColumnMenu, cell: column.cell, headerSelectionValue: column.id === 'selected' ? headerSelectionValue : undefined, sortable: column.sortable, width: column.width || calculatedWidths[column.field] || 150, className: column.className, headerClassName: column.headerClassName }))))))),
        modeIa === ModeIA.Teaser && !currentUser.isInspectpointUser && React.createElement(IaTeaserModeBanner, { bannerMode: "review" }),
        React.createElement(BulkCompleteReviewModal, { isOpen: showBulkCompleteModal, inspections: selectedInspections, selectAll: selectAll, totalCount: totalCount, filters: filters, onClose: handleBulkCompleteClose })));
}
export default AiToReviewInspectionsIndex;
